import React, { useState, useEffect, useCallback } from 'react'
import {
  Container,
  Card,
  Button,
  Form,
} from 'react-bootstrap'
import 'bootstrap/dist/css/bootstrap.min.css'
import toast from 'react-hot-toast'
import { useAuth } from './AuthContext'
import { 
  FaEye, 
  FaEyeSlash, 
  FaUserShield, 
  FaChalkboardTeacher, 
  FaCalculator,
  FaBullhorn,
  FaUserFriends,
  FaApple,
  FaGooglePlay,
} from 'react-icons/fa'
import { useNavigate, useLocation } from 'react-router-dom'
import { GoogleLogin } from '@react-oauth/google'
import { FcGoogle } from 'react-icons/fc'
import { LuGraduationCap } from 'react-icons/lu'
import { NO_IMAGE_SRC } from '../utils/instituteBranding'
import { hasValidInstituteIdCookie } from '../utils/instituteIdCookie'
import { BUS_ATTENDANT_HOME_ROUTE, isBusAttendantUser } from '../utils/busAttendant'
import { GOOGLE_CLIENT_ID } from '../config/googleAuth'
import { REGISTRATION_RESULT_KEY } from './RegistrationWizard'

export default function LoginSignup() {
  
  const { user, login, loginWithGoogle, signUp } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [marginLeft, setMarginLeft] = useState(
    window.innerWidth >= 992 ? '270px' : '0px',
  )

  useEffect(() => {// CRA inlines this at build time — if undefined, .env wasn't picked up
    

    if (!user) return

    if (window.location.pathname === '/LoginSignup') {
      // Check if admin user has selected an institute before redirecting
      // If no institute_id cookie exists, stay on LoginSignup (user clicked Cancel)
      if (user?.user_type === 'admin') {
        // Only redirect to Dashboard if a valid institute is selected
        if (hasValidInstituteIdCookie()) {
          navigate('/Dashboard', { replace: true })
        }
        // If no institute_id, stay on LoginSignup (user needs to select institute or login again)
      } else if (!hasValidInstituteIdCookie()) {
        // Invalid/missing institute (e.g. institute_id "0") — stay on login; InstituteContext logs out non-admins
      } else if (user?.user_type === 'teacher') {
        navigate('/AssignClass', { replace: true })
      } else if (user?.user_type === 'parents' || user?.user_type === 'parent') {
        navigate('/parent/dashboard', { replace: true })
      } else if (user?.user_type === 'accountant') {
        navigate('/FeeCollection', { replace: true })
      } else if (['fee_collector', 'fees_collector'].includes(user?.user_type)) {
        navigate('/PendingFee', { replace: true })
      } else if (user?.user_type === 'marketer') {
        navigate('/EnquiryForm', { replace: true })
      } else if (isBusAttendantUser(user)) {
        navigate(BUS_ATTENDANT_HOME_ROUTE, { replace: true })
      } else if (user?.user_type === 'staff') {
        navigate('/TopAnnouncements', { replace: true })
      } else {
        // Fallback for newly introduced user types.
        navigate('/TopAnnouncements', { replace: true })
      }
    }
  }, [user, navigate])

  useEffect(() => {
    const handleResize = () => {
      setMarginLeft(window.innerWidth >= 992 ? '270px' : '0px')
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const [isLogin, setIsLogin] = useState(true)
  const [formData, setFormData] = useState({
    // name: '',
    email: '',
    password: '',
    confirm: '',
    user_type: '',
  })


  // const   handleParentLogin = () =>{
  //   navigate("/Parent/Registration")
  // }
  

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.email || !formData.password) {
      toast.error('All fields are required')
      return
    }
    if (!isLogin && formData.password !== formData.confirm) {
      toast.error('Passwords do not match.')
      return
    }
    if (isLogin) {
      login({
        user_type: formData.user_type,
        email: formData.email,
        password: formData.password,
      })
    } else {
      signUp({
        user_type: formData.user_type,
        // name: formData.name,
        email: formData.email,
        password: formData.password,
      })
    }
  }
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [generatedPasswordMessage, setGeneratedPasswordMessage] = useState('')

  useEffect(() => {
    let registrationResult = location.state?.fromRegistration
      ? {
          email: location.state.email,
          password: location.state.password,
        }
      : null

    if (!registrationResult) {
      try {
        const raw = sessionStorage.getItem(REGISTRATION_RESULT_KEY)
        if (raw) {
          registrationResult = JSON.parse(raw)
        }
      } catch (_) {
        registrationResult = null
      }
    }

    if (!registrationResult?.password && !registrationResult?.email) return

    setFormData((prev) => ({
      ...prev,
      email: registrationResult.email || prev.email,
      password: registrationResult.password || prev.password,
    }))

    if (registrationResult.password) {
      setGeneratedPasswordMessage(registrationResult.password)
    }

    try {
      sessionStorage.removeItem(REGISTRATION_RESULT_KEY)
    } catch (_) {
      // ignore
    }

    if (location.state?.fromRegistration) {
      navigate(location.pathname, { replace: true, state: null })
    }
  }, [location, navigate])
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (window.innerWidth < 992) return undefined

    const html = document.documentElement
    const body = document.body
    const prevHtmlOverflow = html.style.overflow
    const prevBodyOverflow = body.style.overflow
    html.style.overflow = 'hidden'
    body.style.overflow = 'hidden'

    const onResize = () => {
      if (window.innerWidth < 992) {
        html.style.overflow = prevHtmlOverflow
        body.style.overflow = prevBodyOverflow
      } else {
        html.style.overflow = 'hidden'
        body.style.overflow = 'hidden'
      }
    }
    window.addEventListener('resize', onResize)

    return () => {
      window.removeEventListener('resize', onResize)
      html.style.overflow = prevHtmlOverflow
      body.style.overflow = prevBodyOverflow
    }
  }, [])

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword)
  }

  // True when this page is rendered inside a React Native WebView.
  // Google blocks the GSI iframe (<GoogleLogin>) inside embedded webviews,
  // so we fall back to a native sign-in flow bridged over postMessage.
  const isReactNativeWebView =
    typeof window !== 'undefined' && !!window.ReactNativeWebView

  // Hide Google Sign when opened inside the iOS native app WebView.
  const isIOSApp =
    isReactNativeWebView &&
    typeof navigator !== 'undefined' &&
    /iPad|iPhone|iPod/.test(navigator.userAgent)

  // Shared handler: takes a Google ID token (credential) and logs in.
  const processGoogleCredential = useCallback(
    async (credential) => {
      if (!credential) {
        toast.error('Google sign-in failed. Please try again.')
        setIsGoogleLoading(false)
        return
      }

      setIsGoogleLoading(true)
      try {
        await loginWithGoogle(credential)
      } finally {
        setIsGoogleLoading(false)
      }
    },
    [loginWithGoogle],
  )

  const handleGoogleSuccess = (credentialResponse) => {
    processGoogleCredential(credentialResponse?.credential)
  }

  const handleGoogleError = () => {
    toast.error('Google sign-in was cancelled or failed.')
  }

  // Ask the React Native host app to run its native Google sign-in flow.
  // RN must listen for the { type: 'GOOGLE_SIGN_IN_REQUEST' } message and
  // reply with the result (see the listener below for the reply contract).
  const requestNativeGoogleSignIn = () => {
    if (!window.ReactNativeWebView) {
      toast.error('Google sign-in is unavailable in this app.')
      return
    }
    setIsGoogleLoading(true)
    window.ReactNativeWebView.postMessage(
      JSON.stringify({ type: 'GOOGLE_SIGN_IN_REQUEST' }),
    )
  }

  // Receive the native sign-in result from the React Native host app.
  //
  // Message contract (RN -> WebView), delivered either via
  // `webViewRef.injectJavaScript(...)` dispatching a 'message' event or via
  // `webViewRef.postMessage(...)`. The payload should be JSON:
  //   Success: { type: 'GOOGLE_SIGN_IN_RESULT', credential: '<google id token>' }
  //   Failure: { type: 'GOOGLE_SIGN_IN_ERROR', error: '<optional message>' }
  useEffect(() => {
    if (!isReactNativeWebView) return undefined

    const handleNativeMessage = (event) => {
      let payload = event?.data
      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload)
        } catch (_) {
          return
        }
      }
      if (!payload || typeof payload !== 'object') return

      if (payload.type === 'GOOGLE_SIGN_IN_RESULT') {
        processGoogleCredential(payload.credential || payload.idToken)
      } else if (payload.type === 'GOOGLE_SIGN_IN_ERROR') {
        setIsGoogleLoading(false)
        toast.error(payload.error || 'Google sign-in was cancelled or failed.')
      }
    }

    // Global hook so RN can also deliver the token directly via injectJavaScript,
    // e.g. `window.onNativeGoogleSignIn({ type: 'GOOGLE_SIGN_IN_RESULT', credential })`.
    window.onNativeGoogleSignIn = (payload) =>
      handleNativeMessage({ data: payload })

    // iOS RN WebView fires on `window`, Android often fires on `document`.
    window.addEventListener('message', handleNativeMessage)
    document.addEventListener('message', handleNativeMessage)

    return () => {
      window.removeEventListener('message', handleNativeMessage)
      document.removeEventListener('message', handleNativeMessage)
      if (window.onNativeGoogleSignIn) {
        delete window.onNativeGoogleSignIn
      }
    }
  }, [isReactNativeWebView, processGoogleCredential])

  // const getUserTypeIcon = (userType) => {
  //   switch(userType) {
  //     case 'admin':
  //       return <FaUserShield size={14} />
  //     case 'teacher':
  //       return <FaChalkboardTeacher size={14} />
  //     case 'accountant':
  //       return <FaCalculator size={14} />
  //     case 'marketer':
  //       return <FaBullhorn size={14} />
  //     case 'parents':
  //       return <FaUserFriends size={14} />
  //     default:
  //       return <FaUserShield size={14} />
  //   }
  // }

  const storeDownloadButtons = (extraClass = '') =>
    isReactNativeWebView ? null : (
      <div
        className={`store-download-wrap ${extraClass}`.trim()}
        aria-label="Download the Vibhum EduSys mobile app"
      >
        <p className="store-download-label">We are also available on</p>
        <div className="store-download-btns">
          <a
            className="store-download-btn play-btn"
            href="https://play.google.com/store/apps/details?id=com.vibhum.edusys"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaGooglePlay size={16} aria-hidden="true" />
            <span>Google Play</span>
          </a>
          <a
            className="store-download-btn apple-btn"
            href="https://apps.apple.com/in/app/vibhum-edusys/id6760622621"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaApple size={18} aria-hidden="true" />
            <span>App Store</span>
          </a>
        </div>
      </div>
    )

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          .fade-in {
            animation: fadeIn 0.5s ease-out;
          }

            .login-container {
            background: #F3F4F6;
            display: flex;
            flex-direction: row;
            align-items: stretch;
            justify-content: center;
            box-sizing: border-box;
            width: 100%;
            max-width: 100%;
            overflow-x: hidden;
            /* Offset app shell vertical padding so content is not clipped */
            margin-top: -12px;
            margin-bottom: -12px;
            min-height: calc(100vh + 24px);
            min-height: calc(100dvh + 24px);
          }

          .login-card .form-control,
          .login-card .form-select,
          .login-card .submit-button {
            max-width: 100%;
            box-sizing: border-box;
          }

          .login-card-wrapper {
            flex: 1 1 auto;
            min-width: 0;
            width: auto;
            max-width: 100%;
            box-sizing: border-box;
          }

          .login-card {
            background: #ffffff;
            border-radius: 24px;
            box-shadow: 0 20px 60px rgba(34, 56, 67, 0.15), 0 0 0 1px rgba(34, 56, 67, 0.05);
            overflow: hidden;
            transition: all 0.3s ease;
            margin-bottom: 0rem;
          }

          .login-card:hover {
            box-shadow: 0 25px 70px rgba(34, 56, 67, 0.2), 0 0 0 1px rgba(34, 56, 67, 0.1);
          }

          .form-input {
            border: 2px solid #E5E7EB;
            border-radius: 10px;
            padding: 8px 12px;
            font-size: 13px;
            transition: all 0.2s ease;
            font-family: 'Inter', system-ui, sans-serif;
            color: #223843;
            background-color: #ffffff;
          }

          .form-input:focus {
            border-color: #223843;
            box-shadow: 0 0 0 3px rgba(34, 56, 67, 0.1);
            outline: none;
            color: #223843;
          }

          .form-input::placeholder {
            color: #9CA3AF;
          }

          .form-select {
            background-color: #ffffff;
            color: #223843;
            border: 2px solid #E5E7EB;
            border-radius: 10px;
            padding: 8px 12px;
            font-size: 13px;
            transition: all 0.2s ease;
            font-family: 'Inter', system-ui, sans-serif;
            cursor: pointer;
            appearance: none;
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='none' stroke='%23223843' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3E%3C/svg%3E");
            background-repeat: no-repeat;
            background-position: right 12px center;
            background-size: 16px;
            padding-right: 40px;
          }

          .form-select:focus {
            border-color: #223843;
            box-shadow: 0 0 0 3px rgba(34, 56, 67, 0.1);
            outline: none;
            background-color: #ffffff;
            color: #223843;
          }

          .form-select:hover {
            background-color: #ffffff;
            border-color: #223843;
            color: #223843;
          }

          .form-select option {
            background-color: #ffffff;
            color: #223843;
            padding: 10px;
          }

          .form-select option:hover {
            background-color: #F3F4F6;
            color: #223843;
          }

          .form-select option:checked {
            background-color: #223843 !important;
            color: #ffffff !important;
          }

          .form-select option:focus {
            background-color: #223843 !important;
            color: #ffffff !important;
          }

          .form-select option[selected] {
            background-color: #223843 !important;
            color: #ffffff !important;
          }

          .form-select option[value]:checked {
            background-color: #223843 !important;
            color: #ffffff !important;
          }

          .toggle-container {
            background: #E5E7EB;
            border-radius: 10px;
            padding: 3px;
            display: flex;
            margin-bottom: 16px;
          }

          .toggle-button {
            flex: 1;
            padding: 8px 16px;
            border: none;
            background: transparent;
            border-radius: 7px;
            font-weight: 600;
            font-size: 13px;
            color: #223843;
            transition: all 0.2s ease;
            cursor: pointer;
            font-family: 'Inter', system-ui, sans-serif;
          }

          .toggle-button.active {
            background: #223843;
            color: #ffffff;
            box-shadow: 0 2px 8px rgba(34, 56, 67, 0.3);
          }

          .auth-action-row {
            display: flex;
            align-items: stretch;
            gap: 10px;
            width: 100%;
            margin-top: 4px;
          }

          .auth-action-row .submit-button,
          .auth-action-row .signup-wizard-button {
            flex: 1 1 0;
            width: auto;
            min-width: 0;
            margin-top: 0;
          }

          .submit-button {
            background: #223843;
            border: none;
            border-radius: 10px;
            padding: 10px 16px;
            font-weight: 600;
            font-size: 14px;
            color: #ffffff;
            width: 100%;
            transition: all 0.2s ease;
            font-family: 'Inter', system-ui, sans-serif;
          }

          .submit-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 20px rgba(34, 56, 67, 0.4);
            background: #1a2d35;
          }

          .submit-button:active {
            transform: translateY(0);
          }

          .signup-wizard-button {
            background: #ffffff;
            border: 2px solid #223843;
            border-radius: 10px;
            padding: 9px 16px;
            font-weight: 600;
            font-size: 14px;
            color: #223843;
            width: 100%;
            transition: all 0.2s ease;
            font-family: 'Inter', system-ui, sans-serif;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
          }

          .signup-wizard-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 20px rgba(34, 56, 67, 0.18);
            background: #223843;
            color: #ffffff;
            border-color: #223843;
          }

          .signup-wizard-button:active {
            transform: translateY(0);
          }

          .signup-wizard-button:focus-visible {
            outline: 2px solid #223843;
            outline-offset: 2px;
          }

          .generated-password-message {
            margin-top: 12px;
            padding: 10px 12px;
            border-radius: 10px;
            background: #F3F4F6;
            border: 1px solid #E5E7EB;
            color: #223843;
            font-size: 13px;
            font-family: 'Inter', system-ui, sans-serif;
            line-height: 1.45;
            word-break: break-all;
            animation: fadeIn 0.35s ease;
          }

          .generated-password-message strong {
            font-weight: 700;
            letter-spacing: 0.02em;
          }

          .auth-divider {
            display: flex;
            align-items: center;
            gap: 12px;
            margin: 16px 0;
            color: #9CA3AF;
            font-size: 12px;
            font-family: 'Inter', system-ui, sans-serif;
            font-weight: 500;
          }

          .auth-divider::before,
          .auth-divider::after {
            content: '';
            flex: 1;
            height: 1px;
            background: #E5E7EB;
          }

          .google-login-wrapper {
            display: flex;
            justify-content: center;
            width: 100%;
          }

          .google-login-wrapper > div {
            width: 100% !important;
            display: flex;
            justify-content: center;
          }

          .native-google-button {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            width: 100%;
            max-width: 360px;
            min-height: 44px;
            padding: 10px 16px;
            background: #FFFFFF;
            color: #3C4043;
            font-size: 15px;
            font-weight: 500;
            border: 1px solid #DADCE0;
            border-radius: 4px;
            cursor: pointer;
            transition: background 0.2s ease, box-shadow 0.2s ease;
          }

          .native-google-button:hover:not(:disabled) {
            background: #F7F8F8;
            box-shadow: 0 1px 2px rgba(60, 64, 67, 0.3);
          }

          .native-google-button:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .password-toggle {
            position: absolute;
            right: 10px;
            top: 50%;
            transform: translateY(-50%);
            background: none;
            border: none;
            color: #223843;
            cursor: pointer;
            padding: 2px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: color 0.2s ease;
          }

          .password-toggle:hover {
            color: #1a2d35;
          }

          .user-type-option {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 8px 0;
          }

          .logo-container {
            display: flex;
            justify-content: center;
            margin-bottom: 12px;
            padding-top: 16px;
          }

          .logo-wrapper {
            width: 90px;
            height: 90px;
            border-radius: 50%;
            border: 2px solid #000000;
            // background: linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
          }

          .logo-wrapper img {
            width: 100%;
            height: 100%;
            object-fit: contain;
            padding: 8px;
          }

          .login-page-footer {
            position: fixed;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: 20;
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            justify-content: center;
            gap: 4px 8px;
            padding: 8px 12px;
            padding-bottom: max(8px, env(safe-area-inset-bottom, 0px));
            background: rgba(243, 244, 246, 0.95);
            backdrop-filter: blur(10px);
            border-top: 1px solid rgba(34, 56, 67, 0.08);
            box-sizing: border-box;
          }

          .login-page-footer .powered-by-text {
            font-size: 12px;
            color: #6B7280;
            font-family: 'Inter', system-ui, sans-serif;
            font-weight: 500;
            white-space: nowrap;
            pointer-events: auto;
            position: relative;
            z-index: 2;
          }

          .login-page-footer .vibhum-logo-link {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            text-decoration: none;
            margin-left: -1.8rem !important;
            position: relative;
            z-index: 1;
          }

          .login-page-footer .vibhum-logo-img {
            width: 108px; /* visually balanced (not too big) */
            height: auto;
            max-height: 36px;
            object-fit: contain;
            opacity: 0.95;
            transition: transform 0.2s ease, opacity 0.2s ease;
          }

          .login-page-footer .vibhum-logo-link:hover .vibhum-logo-img {
            transform: scale(1.03);
            opacity: 1;
          }

          .gradient-sidebar {
            background: #F3F4F6;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 24px 32px;
            position: relative;
            overflow: hidden;
            flex: 0 0 50%;
            max-width: 50%;
            min-height: 100vh;
            min-height: 100dvh;
            box-sizing: border-box;
          }

          .gradient-sidebar::before {
            content: '';
            position: absolute;
            width: 200%;
            height: 200%;
            // background: radial-gradient(circle, rgba(37, 99, 235, 0.15) 0%, transparent 70%);
            animation: rotate 20s linear infinite;
          }

          @keyframes rotate {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }

          .sidebar-content {
            position: relative;
            z-index: 1;
            text-align: center;
            color: #223843;
          }

          .mobile-welcome {
            display: none;
            width: 100%;
            text-align: center;
            color: #223843;
          }

          .store-download-wrap {
            display: flex;
            flex-direction: column;
            align-items: center;
            box-sizing: border-box;
          }

          .store-download-label {
            margin: 0 0 10px;
            font-size: 13px;
            font-weight: 500;
            color: #223843;
            opacity: 0.8;
            font-family: 'Inter', system-ui, sans-serif;
            text-align: center;
          }

          .store-download-btns {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            justify-content: center;
            gap: 10px;
            box-sizing: border-box;
          }

          .store-download-btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            min-width: 148px;
            padding: 10px 16px;
            border-radius: 10px;
            font-family: 'Inter', system-ui, sans-serif;
            font-size: 13px;
            font-weight: 600;
            text-decoration: none;
            line-height: 1;
            transition: all 0.2s ease;
            box-sizing: border-box;
          }

          .store-download-btn.play-btn {
            background: #223843;
            color: #ffffff;
            border: 2px solid #223843;
          }

          .store-download-btn.apple-btn {
            background: #ffffff;
            color: #223843;
            border: 2px solid #223843;
          }

          .store-download-btn:hover {
            transform: translateY(-2px);
            text-decoration: none;
          }

          .store-download-btn.play-btn:hover {
            background: #1a2d35;
            border-color: #1a2d35;
            color: #ffffff;
            box-shadow: 0 8px 20px rgba(34, 56, 67, 0.35);
          }

          .store-download-btn.apple-btn:hover {
            background: #223843;
            color: #ffffff;
            box-shadow: 0 8px 20px rgba(34, 56, 67, 0.18);
          }

          .store-download-btns-sidebar {
            margin-top: 24px;
          }

          .store-download-btns-after-card {
            display: none;
          }

          @media (max-width: 991.98px) {
            .login-container {
              padding: 12px 16px;
              padding-top: max(12px, env(safe-area-inset-top, 0px));
              padding-bottom: calc(72px + env(safe-area-inset-bottom, 0px));
              flex-direction: column;
              align-items: stretch;
              justify-content: flex-start;
              min-height: calc(100vh + 24px);
              min-height: calc(100dvh + 24px);
              height: auto;
              overflow-y: auto;
              overflow-x: hidden;
              -webkit-overflow-scrolling: touch;
            }

            .mobile-welcome {
              display: block;
              padding: 8px 8px 16px;
              margin: 0 0 12px 0;
              border-bottom: 1px solid rgba(34, 56, 67, 0.08);
              flex-shrink: 0;
            }

            .mobile-welcome-icon {
              width: 64px !important;
              height: 64px !important;
              margin-bottom: 12px !important;
            }

            .login-card-wrapper {
              flex: 0 0 auto;
              width: 100%;
              max-width: 100%;
              margin-top: 0 !important;
              padding-left: 0 !important;
              padding-right: 0 !important;
            }
            
            .login-card {
              border-radius: 20px;
              max-width: 100%;
            }

            .store-download-btns-after-card {
              display: flex;
              flex-direction: column;
              align-items: center;
              padding: 8px 8px 12px;
              flex-shrink: 0;
            }

            .store-download-btn {
              min-width: 136px;
              padding: 9px 14px;
              font-size: 12px;
            }

            .gradient-sidebar {
              display: none !important;
            }

            .logo-wrapper {
              width: 72px;
              height: 72px;
            }

            .logo-container {
              margin-bottom: 8px;
              padding-top: 8px;
            }

            .login-page-footer .vibhum-logo-img {
              width: 88px;
              max-height: 30px;
            }

            .login-page-footer .vibhum-logo-link {
              margin-left: 0 !important;
            }
          }

          @media (max-width: 380px) {
            .login-container {
              padding-left: 12px;
              padding-right: 12px;
            }

            .toggle-button {
              padding: 8px 10px;
              font-size: 12px;
            }

            .login-page-footer .powered-by-text {
              font-size: 11px;
              white-space: normal;
              text-align: center;
            }

            .login-page-footer .vibhum-logo-img {
              width: 76px;
              max-height: 28px;
            }

            .store-download-btns {
              gap: 8px;
            }

            .store-download-btn {
              min-width: 0;
              flex: 1 1 130px;
              padding: 8px 12px;
            }
          }

          @media (max-height: 700px) and (max-width: 991.98px) {
            .mobile-welcome-icon {
              width: 52px !important;
              height: 52px !important;
              margin-bottom: 8px !important;
            }

            .mobile-welcome h1 {
              font-size: 20px !important;
              margin-bottom: 8px !important;
            }

            .mobile-welcome p {
              font-size: 13px !important;
            }

            .logo-wrapper {
              width: 60px;
              height: 60px;
            }

            .logo-container {
              padding-top: 4px;
              margin-bottom: 4px;
            }
          }

          @media (min-width: 992px) {
            .login-container {
              flex: 1 1 auto;
              min-height: 0;
              height: 100%;
              max-height: 100dvh;
              margin-top: 0;
              margin-bottom: 0;
              padding-bottom: 0;
              overflow: hidden;
            }

            .gradient-sidebar {
              min-height: 0;
              align-self: stretch;
            }

            .login-card-wrapper {
              flex: 1 1 50%;
              max-width: 50%;
              min-height: 0;
            }

            .store-download-btns-sidebar {
              margin-top: 32px;
            }

            .store-download-label {
              font-size: 18px;
              font-weight: 600;
              opacity: 1;
              margin-bottom: 14px;
              color: #223843;
            }

            .store-download-btns {
              gap: 14px;
            }

            .store-download-btn {
              min-width: 180px;
              padding: 12px 22px;
              font-size: 15px;
              gap: 10px;
            }

            .store-download-btn svg {
              width: 20px;
              height: 20px;
            }
          }
        `}
      </style>
      <div className="login-container">
        {/* Gradient Sidebar - Desktop Only */}
        <div className="gradient-sidebar d-none d-lg-flex">
          <div className="sidebar-content">
            <div style={{ marginBottom: '20px' }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: '#F3F4F6',
                border: '2px solid #223843',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                backdropFilter: 'blur(10px)',
              }}>
                <LuGraduationCap size={40} style={{ color: '#223843' }} />
              </div>
            </div>
            <h1 className='border-bottom border-2' style={{
              fontSize: 'clamp(22px, 3vw, 28px)',
              fontWeight: '700',
              marginBottom: '12px',
              fontFamily: 'Inter, system-ui, sans-serif',
              color: '#223843',
              borderColor: '#223843 !important',
              paddingBottom: '12px',
            }}>
              Welcome Back!
            </h1>
            <p style={{
              fontSize: '14px',
              color: '#223843',
              opacity: 0.8,
              fontFamily: 'Inter, system-ui, sans-serif',
              maxWidth: '400px',
              margin: '0 auto',
            }}>
              Sign in to access your account and manage your institute activities efficiently.
            </p>
            {storeDownloadButtons('store-download-btns-sidebar')}
          </div>
        </div>

        {/* Mobile Welcome (mobile-only) */}
        <div className="mobile-welcome">
          <div style={{ marginBottom: '2px' }}>
            <div
              className="mobile-welcome-icon"
              style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: '#F3F4F6',
              border: '2px solid #223843',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 18px',
              backdropFilter: 'blur(10px)',
            }}>
              <LuGraduationCap size={40} style={{ color: '#223843' }} />
            </div>
          </div>

          <h1 className='border-bottom border-2' style={{
            fontSize: 'clamp(22px, 3vw, 28px)',
            fontWeight: '700',
            marginBottom: '12px',
            fontFamily: 'Inter, system-ui, sans-serif',
            color: '#223843',
            borderColor: '#223843 !important',
            paddingBottom: '2px',
          }}>
            Welcome Back!
          </h1>

          <p style={{
            fontSize: '14px',
            color: '#223843',
            opacity: 0.8,
            fontFamily: 'Inter, system-ui, sans-serif',
            maxWidth: '400px',
            margin: '0 auto',
          }}>
            Sign in to access your account and manage your school activities efficiently.
          </p>
        </div>

        {/* Login Card */}
        <div
          className="login-card-wrapper d-flex align-items-center justify-content-center"
          style={{
            padding: isMobile ? '0 0 8px' : '20px',
            marginTop: '0px',
          }}
        >
          <Card className="login-card fade-in" style={{
            width: '100%',
            maxWidth: '420px',
            border: 'none',
          }}>
            <Card.Body
              style={{
                padding: isMobile ? '24px 20px' : '32px 28px',
              }}
            >
              <div className="logo-container">
                <div className="logo-wrapper">
                  <img src={NO_IMAGE_SRC} alt="Institute logo" />
                </div>
              </div>
              {/* Title */}
              <h2 className="text-center mb-2" style={{
                fontSize: isMobile ? '20px' : '22px',
                fontWeight: '700',
                color: '#223843',
                fontFamily: 'Inter, system-ui, sans-serif',
                marginBottom: '4px',
              }}>
                {/* {isLogin ? 'Welcome Back!' : 'Create Account'} */}
                {isLogin ? 'Sign in to continue' : 'Sign up to get started'}

              </h2>
              <p className="text-center mb-4" style={{
                color: '#223843',
                opacity: 0.7,
                fontSize: '13px',
                fontFamily: 'Inter, system-ui, sans-serif',
                marginBottom: '20px',
              }}>
                {/* {isLogin ? 'Sign in to continue' : 'Sign up to get started'} */}
              </p>

              <Form onSubmit={handleSubmit} className='text-start'>
                {/* User Type Selection */}
                {/* <Form.Group className="mb-3 text">
                  <Form.Label style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#223843',
                    marginBottom: '6px',
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}>
                    Login As
                  </Form.Label>
                  <Form.Select
                    name="user_type"
                    value={formData.user_type}
                    onChange={handleChange}
                    className="form-select"
                    required
                  >
                    <option value="">Select User Type</option>
                    <option value="admin">Admin</option>
                    <option value="teacher">Teacher</option>
                    <option value="accountant">Accountant</option>
                    <option value="marketer">Marketer</option>
                    <option value="parents">Guardian</option>
                  </Form.Select>
                  {formData.user_type && (
                    <div style={{
                      marginTop: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      color: '#223843',
                      fontSize: '12px',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}>
                      {getUserTypeIcon(formData.user_type)}
                      <span style={{ fontWeight: '500' }}>
                        {formData.user_type.charAt(0).toUpperCase() + formData.user_type.slice(1)}
                      </span>
                    </div>
                  )}
                </Form.Group> */}

                {/* Name Field - Signup Only */}
                {!isLogin && (
                  <Form.Group className="mb-2">
                    <Form.Label style={{
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#223843',
                      marginBottom: '6px',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}>
                      Full Name
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name || ''}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      className="form-input"
                      required
                    />
                  </Form.Group>
                )}

                {/* Email Field */}
                <Form.Group className="mb-2">
                  <Form.Label style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#223843',
                    marginBottom: '6px',
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}>
                    Email Address
                  </Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    className="form-input"
                    required
                  />
                </Form.Group>

                {/* Password Field */}
                <Form.Group className="mb-2">
                  <Form.Label style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#223843',
                    marginBottom: '6px',
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}>
                    Password
                  </Form.Label>
                  <div className="position-relative">
                    <Form.Control
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Enter your password"
                      className="form-input"
                      style={{ paddingRight: '40px' }}
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={togglePasswordVisibility}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <FaEyeSlash size={16} />
                      ) : (
                        <FaEye size={16} />
                      )}
                    </button>
                  </div>
                </Form.Group>

                {/* Confirm Password Field - Signup Only */}
                {!isLogin && (
                  <Form.Group className="mb-2">
                    <Form.Label style={{
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#223843',
                      marginBottom: '6px',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}>
                      Confirm Password
                    </Form.Label>
                    <div className="position-relative">
                      <Form.Control
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirm"
                        value={formData.confirm}
                        onChange={handleChange}
                        placeholder="Confirm your password"
                        className="form-input"
                        style={{ paddingRight: '40px' }}
                        required
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={toggleConfirmPasswordVisibility}
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showConfirmPassword ? (
                          <FaEyeSlash size={16} />
                        ) : (
                          <FaEye size={16} />
                        )}
                      </button>
                    </div>
                  </Form.Group>
                )}

                {/* Submit / Sign Up Buttons */}
                {isLogin ? (
                  <>
                    <Button
                      type="submit"
                      className="submit-button"
                      disabled={isGoogleLoading}
                    >
                      Sign In
                    </Button>
                    {generatedPasswordMessage && (
                      <div
                        className="generated-password-message"
                        role="status"
                        aria-live="polite"
                      >
                        Your generated password is:{' '}
                        <strong>{generatedPasswordMessage}</strong>
                      </div>
                    )}
                  </>
                ) : (
                  <Button
                    type="submit"
                    className="submit-button"
                    disabled={isGoogleLoading}
                  >
                    Create Account
                  </Button>
                )}

                {isLogin && (GOOGLE_CLIENT_ID || isReactNativeWebView) && (
                  <>
                    {!isIOSApp && (
                      <>
                        <div className="auth-divider">or</div>
                        <div className="google-login-wrapper">
                          {isReactNativeWebView ? (
                            <button
                              type="button"
                              className="native-google-button"
                              onClick={requestNativeGoogleSignIn}
                              disabled={isGoogleLoading}
                            >
                              <FcGoogle size={20} aria-hidden="true" />
                              <span>
                                {isGoogleLoading
                                  ? 'Signing in…'
                                  : 'Continue with Google'}
                              </span>
                            </button>
                          ) : (
                            <GoogleLogin
                              clientId={GOOGLE_CLIENT_ID}
                              onSuccess={handleGoogleSuccess}
                              onError={handleGoogleError}
                              useOneTap={false}
                              theme="outline"
                              size="large"
                              text="continue_with"
                              shape="rectangular"
                              width={isMobile ? '100%' : '360'}
                              locale="en"
                            />
                          )}
                        </div>
                      </>
                    )}
                    <Button
                      type="button"
                      className="signup-wizard-button"
                      onClick={() => navigate('/SignUp')}
                      disabled={isGoogleLoading}
                      style={{ marginTop: '14px' }}
                    >
                      <i className="bi bi-person-plus" aria-hidden="true" />
                      New User
                    </Button>
                  </>
                )}
              </Form>
            </Card.Body>
          </Card>
        </div>
        {storeDownloadButtons('store-download-btns-after-card')}
      </div>

      <footer className="login-page-footer" aria-label="Login page footer">
        <span className="powered-by-text">Powered by <strong>VibhumEduSys</strong></span>
        <a
          className="vibhum-logo-link"
          href="https://vibhumedusys.com/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="VibhumEduSys (opens in a new tab)"
        >
          <img
            className="vibhum-logo-img"
            src="/poweredby_logo.webp"
            alt="VibhumEduSys logo"
          />
        </a>
      </footer>
      {/* <CSSTransition
          in={openForgotModal}
          timeout={500}
          classNames="slide-right-left"
          unmountOnExit
          nodeRef={forgotRef}
        >
          <div
            ref={forgotRef}
            style={
              isMobileForgotPass
                ? { marginTop: '10px', zIndex: 1051 }
                : {
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 1050,
                  }
            }
          >
            <ForgotPassword
              hide={() => setOpenForgotModal(false)}
              data={formData}
            />
          </div>
        </CSSTransition> */}
    </>
  )
}
